import React, { useState } from 'react';
import { Card } from '../../../../components/Card/Card';
import { withSuspendedCard } from '../../../../util/suspense';
import { FormSwitcher } from '../../../../components/Form/form_switcher/form_switcher';
import { Stack } from '../../../../components/Stack/Stack';
import { graphql } from '../../../../generated';
import { NetworkStatus, useMutation, useSuspenseQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { useQueryAsState } from '../../../../model/history';
import { t } from '../../../../locales/translate';
import { Button } from '../../../../components/Button/Button';
import { Icon } from '../../../../components/Icon/Icon';
import { Box } from '../../../../components/Box/box';
import { useModalPresenter } from '../../../../components/Modal/PresentedModal';
import { ProductsAndEventsCreate } from './create';
import { QueryNames } from '../../../../model/graphql';
import { useConfirmation } from '../../../../components/ConfirmationPrompt/ConfirmationPrompt';
import { ProductEventTable } from '../../../../components/product_event_table/product_event_table';
import { ProductEventTableActions } from '../../../../components/product_event_table/product_event_table_actions';
import { useProductEventTableSelection } from '../../../../model/product_event_table_selection';
import { serializeQuery } from '../../../../model/filter';

const VOUCHER_CAMPAIGN_PRODUCT_EVENTS = graphql(`
  query GetProductsAndEventsByVoucherCampaignId(
    $id: ID!
    $type: ProductType
    $terms: [String!]
    $first: Int
    $after: String
    $categoryIds: [ID!]
  ) {
    voucherCampaign(id: $id) {
      id
      shop {
        id
        name
      }
      permissions {
        id
        updateProductVariants
      }
    }
    products(
      voucherCampaignId: $id
      type: $type
      terms: $terms
      first: $first
      after: $after
      categoryIds: $categoryIds
    ) {
      edges {
        node {
          id
          sku
          artist
          name
          ... on Event {
            venue {
              id
              name
            }
            happeningFrom
          }
          ... on MerchProduct {
            categories {
              nodes {
                id
                name
              }
            }
            productType
            color
            price(countryCode: "de")
            estimatedAvailability
          }
          productVariantsForVoucherCampaign(voucherCampaignId: $id) {
            id
            sku
            description
            estimatedAvailability
            netPrice
            price
            pricingCategory {
              id
              name
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

const MERCH_PRODUCT_CATEGORIES = graphql(`
  query GetMerchProductCategoriesForVoucherCampaign($first: Int, $voucherCampaignId: ID) {
    categories(first: $first, voucherCampaignId: $voucherCampaignId, withProductTypes: [MERCH_PRODUCT]) {
      nodes {
        id
        name
      }
    }
  }
`);

const REMOVE_PRODUCT_VARIANTS = graphql(`
  mutation RemoveProductVariants($voucherCampaignId: ID!, $productVariantIds: [ID!]!) {
    removeProductVariantsFromVoucherCampaign(
      input: { voucherCampaignId: $voucherCampaignId, productVariantIds: $productVariantIds }
    ) {
      voucherCampaign {
        id
      }
    }
  }
`);

const REMOVE_ALL_PRODUCT_VARIANTS = graphql(`
  mutation RemoveAllProductVariants(
    $voucherCampaignId: ID!
    $terms: [String!]
    $categoryIds: [ID!]
    $productType: ProductType
  ) {
    removeFilteredSelectionFromVoucherCampaign(
      input: {
        voucherCampaignId: $voucherCampaignId
        terms: $terms
        categoryIds: $categoryIds
        productType: $productType
      }
    ) {
      voucherCampaign {
        id
      }
    }
  }
`);

function DiscountedProductsAndEvents$(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const translation = t.page.voucherCampaign.discountedProductsAndEvents;

  const modal = useModalPresenter();
  const [productTab, setProductTab] = useState<boolean>(true);
  const [params, setParams] = useQueryAsState();
  const { data, networkStatus, fetchMore } = useSuspenseQuery(VOUCHER_CAMPAIGN_PRODUCT_EVENTS, {
    variables: {
      id,
      categoryIds: params.discountedCategories,
      terms: (params.discountedQuery && serializeQuery(params.discountedQuery, true)) || undefined,
      type: productTab ? 'MERCH_PRODUCT' : 'EVENT',
      first: 10,
    },
  });
  const { data: categoriesData } = useSuspenseQuery(MERCH_PRODUCT_CATEGORIES, {
    variables: {
      voucherCampaignId: id,
      first: 1000,
    },
  });
  const categories = categoriesData.categories.nodes;
  const { pageInfo } = data.products;

  const {
    items,
    size,
    handleRowSelect,
    handleSelectAllDisplayed,
    handleSelectAllItems,
    clear,
    selectionType,
    allSelected,
    mappedItems,
  } = useProductEventTableSelection(data.products, categories);
  const actionTranslations = {
    single: {
      card: translation.singleResultSelected,
      confirm: translation.delete.single,
    },
    multiple: {
      card: translation.manyResultsSelected(size),
      confirm: translation.delete.multiple(size),
    },
    all: {
      card: translation.allSelected,
      confirm: translation.delete.all,
    },
  };

  // Mutations
  const refetchQueries = [QueryNames.VOUCHER_CAMPAIGN_PRODUCT_EVENTS, QueryNames.MERCH_PRODUCT_CATEGORIES];

  const [destroy] = useMutation(REMOVE_PRODUCT_VARIANTS, {
    refetchQueries,
  });
  const [destroyAll] = useMutation(REMOVE_ALL_PRODUCT_VARIANTS, {
    refetchQueries,
  });

  const { confirm } = useConfirmation({
    message: actionTranslations[selectionType].confirm,
    actionName: translation.delete.confirm,
    action: async () => {
      if (allSelected) {
        await destroyAll({
          variables: {
            voucherCampaignId: id,
            productType: productTab ? 'MERCH_PRODUCT' : 'EVENT',
          },
        });
        clear();
        return;
      }

      if (size === 0) return;
      await destroy({
        variables: {
          voucherCampaignId: id,
          productVariantIds: items,
        },
      });
      clear();
    },
  });

  let cardActions = (
    <Icon
      name="plus"
      onClick={() =>
        modal.present(
          ProductsAndEventsCreate,
          { voucherCampaignId: id, shopId: data.voucherCampaign.shop.id, productTab },
          { size: 'xl' }
        )
      }
      disabled={!data.voucherCampaign.permissions.updateProductVariants}
    />
  );

  if (size > 0) {
    cardActions = (
      <>
        {!allSelected && (
          <Button title={translation.selectAll} type="secondary-transparent" condensed onClick={handleSelectAllItems} />
        )}
        <Icon name="trash" onClick={confirm} disabled={!data.voucherCampaign.permissions.updateProductVariants} />
      </>
    );
  }

  return (
    <Card
      fullbleed
      title={size === 0 ? translation.title : actionTranslations[selectionType].card}
      actions={cardActions}
      withSelectedItems={size > 0}
      footer={
        pageInfo?.hasNextPage && (
          <Button
            title={t.actions.loadMore}
            onClick={() => {
              fetchMore({
                variables: {
                  after: pageInfo.endCursor,
                },
              });
            }}
          />
        )
      }
    >
      <Box padding={{ top: 10 }}>
        <Stack size="xxxs">
          <Box padding={{ left: 24, right: 24 }}>
            <FormSwitcher
              labels={[translation.merchandise, translation.events]}
              value={productTab}
              onChange={(val) => {
                clear();
                setParams({ discountedCategories: [], discountedQuery: [] });
                setProductTab(!!val);
              }}
              label={translation.switcherType}
            />
          </Box>
          <ProductEventTable
            loading={networkStatus === NetworkStatus.refetch}
            type={productTab ? 'products' : 'events'}
            items={mappedItems}
            selectedVariantIds={items}
            onRowSelect={handleRowSelect}
            onSelectAll={handleSelectAllDisplayed}
            emptyMessage={
              (params.discountedQuery || []).length > 0
                ? translation.table.noSearchResultsFound
                : productTab
                  ? translation.table.emptyProducts(data.voucherCampaign.shop.name)
                  : translation.table.emptyEvents
            }
            actions={
              <ProductEventTableActions
                searchTerms={params.discountedQuery || []}
                categoryOptions={categories.map((e) => ({ id: e.id, label: e.name }))}
                categories={params.discountedCategories || []}
                onCategoryChange={(discountedCategories) => setParams({ discountedCategories })}
                onSearchTermChange={(discountedQuery) => setParams({ discountedQuery, page: 0 })}
                showCategoriesFilter={productTab && categories.length > 0}
              />
            }
          />
        </Stack>
      </Box>
    </Card>
  );
}

export const DiscountedProductsAndEvents = withSuspendedCard(DiscountedProductsAndEvents$, {
  loadingTitle: t.page.voucherCampaign.discountedProductsAndEvents.title,
});
